import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useDenyApproval, useRequestInfoApproval } from "@/hooks/security/useApprovals";
import type { ApprovalRequestDTO } from "@/types/lighthouseApprovals";

interface Props {
  request: ApprovalRequestDTO | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "deny" | "info";
  onDone?: () => void;
}

export function DenyOrInfoDialog({ request, open, onOpenChange, mode, onDone }: Props) {
  const [text, setText] = useState("");
  const deny = useDenyApproval();
  const info = useRequestInfoApproval();
  if (!request) return null;

  const isInfo = mode === "info";
  const submit = () => {
    if (isInfo) {
      if (!text.trim()) return;
      info.mutate({ id: request.id, reason: text }, {
        onSuccess: () => { setText(""); onOpenChange(false); onDone?.(); },
      });
    } else {
      deny.mutate({ id: request.id, reason: text || undefined }, {
        onSuccess: () => { setText(""); onOpenChange(false); onDone?.(); },
      });
    }
  };

  const busy = isInfo ? info.isPending : deny.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isInfo ? "Ask the requester for more info" : "Deny this approval?"}
          </DialogTitle>
          <DialogDescription>
            {isInfo
              ? "Your question will be posted to the conversation and the request moves to info_requested."
              : "Optionally explain why this agent is being denied."}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder={
            isInfo
              ? "What credential paths is this agent expected to read?"
              : "Reason (optional)…"
          }
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || (isInfo && !text.trim())}
            variant={isInfo ? "default" : "destructive"}>
            {isInfo ? "Send" : "Deny"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
